<template lang="html">
  <div class="sign-up-onboarding flex flex-col gap-6">
    <div class="sign-up-onboarding__welcome flex flex-col items-center gap-2 text-center">
      <UIcon :name="brandIcons.logoLargeDark" class="h-14 w-auto" />
      <div class="text-md font-bold">
        Спасибо за регистрацию!
      </div>
      <p class="text-sm text-muted">
        Выполните рекомендуемые шаги, чтобы получать бонусы и промокоды
      </p>
    </div>

    <ApiErrorAlert v-if="serverError" :error="serverError" />

    <div class="sign-up-onboarding__steps flex flex-col gap-3">
      <div
        v-if="isTelegramBotAvailable !== false"
        class="sign-up-onboarding__step flex gap-4 border border-default p-4"
      >
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600"
        >
          <UIcon name="i-lucide-send" class="h-5 w-5" />
        </div>

        <div class="flex min-w-0 flex-1 flex-col gap-2">
          <div class="flex items-start justify-between gap-2">
            <span class="font-medium">Привязать Telegram</span>
            <UBadge
              v-if="isTelegramBound"
              color="success"
              variant="subtle"
              label="Готово"
            />
            <UBadge
              v-else-if="waitingForBind"
              color="warning"
              variant="subtle"
              label="Ожидание"
            />
          </div>

          <p class="text-sm text-muted">
            <template v-if="isTelegramBound">
              Telegram привязан
              <span v-if="telegramUserName">: @{{ telegramUserName }}</span>
            </template>
            <template v-else>
              Получайте эксклюзивные бонусы и промокоды в Telegram
            </template>
          </p>

          <div
            v-if="!isTelegramBound"
            class="flex flex-wrap gap-2"
          >
            <UButton
              label="Привязать Telegram"
              color="tertiary"
              :loading="bindLoading"
              :disabled="bindLoading"
              @click="handleBindTelegram"
            />
            <UButton
              v-if="waitingForBind"
              label="Проверить статус"
              color="neutral"
              variant="outline"
              :loading="checkLoading"
              :disabled="checkLoading || bindLoading"
              @click="handleCheckStatus"
            />
          </div>

          <p
            v-if="waitingForBind && !isTelegramBound"
            class="text-xs text-muted"
          >
            Завершите привязку в Telegram, затем нажмите «Проверить статус»
          </p>
        </div>
      </div>

      <div
        class="sign-up-onboarding__step flex gap-4 border border-default p-4"
      >
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tertiary-50 text-tertiary-600"
        >
          <UIcon name="i-lucide-user" class="h-5 w-5" />
        </div>

        <div class="flex min-w-0 flex-1 flex-col gap-2">
          <div class="flex items-start justify-between gap-2">
            <span class="font-medium">Заполнить профиль</span>
            <UBadge
              v-if="isProfileFilled"
              color="success"
              variant="subtle"
              label="Готово"
            />
          </div>

          <p class="text-sm text-muted">
            Укажите имя и фамилию для персонализации заказов
          </p>

          <UButton
            v-if="!isProfileFilled"
            label="Заполнить профиль"
            color="neutral"
            variant="outline"
            @click="handleGoToProfile"
          />
        </div>
      </div>

      <div
        class="sign-up-onboarding__step flex gap-4 border border-default p-4"
      >
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tertiary-50 text-tertiary-600"
        >
          <UIcon name="i-lucide-wallet" class="h-5 w-5" />
        </div>

        <div class="flex min-w-0 flex-1 flex-col gap-2">
          <span class="font-medium">Мои бонусы</span>
          <p class="text-sm text-muted">
            Следите за балансом бонусов и историей начислений
          </p>

          <UButton
            label="Перейти к бонусам"
            color="neutral"
            variant="outline"
            @click="handleGoToLoyalty"
          />
        </div>
      </div>
    </div>

    <UButton
      label="Продолжить покупки"
      class="w-full justify-center"
      @click="emit('complete')"
    />
  </div>
</template>

<script setup lang="ts">
import ApiErrorAlert from '#Common/components/ApiErrorAlert.vue'
import { useLoyalty } from '#Loyalty/composables/useLoyalty'
import { useTelegramBinding } from '#User/composables/useTelegramBinding'
import { useUser } from '#User/composables/useUser'
import { brandIcons } from '~/app/theme/brand'

const emit = defineEmits<{
  complete: []
}>()

const { store, routing: userRouting } = useUser()
const { routing: loyaltyRouting } = useLoyalty()
const {
  serverError,
  isTelegramBound,
  telegramUserName,
  isTelegramBotAvailable,
  bindLoading,
  checkLoading,
  waitingForBind,
  handleBindTelegram,
  handleCheckStatus,
} = useTelegramBinding()

const isProfileFilled = computed(() => {
  const profile = store.user?.profile
  if (!profile) {
    return false
  }

  return Boolean(profile.name?.trim() && profile.surname?.trim())
})

function handleGoToProfile() {
  emit('complete')
  userRouting.profileEdit.go()
}

function handleGoToLoyalty() {
  emit('complete')
  loyaltyRouting.index.go()
}
</script>
